/**
 * Canvas Utility Functions
 * 
 * Pure utility functions for coordinate conversion and positioning
 */

// Grid size for snap-to-grid
export const GRID_SIZE = 20;

// Default node dimensions for overlap detection
export const NODE_WIDTH = 290;
export const NODE_HEIGHT = 80;
export const MIN_GAP = 20;

// Threshold in pixels to distinguish click from drag
export const DRAG_THRESHOLD = 5;

/**
 * Snap position to grid
 */
export function snapPosition(x, y, snapToGrid = false) {
  if (!snapToGrid) return { x, y };
  return {
    x: Math.round(x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(y / GRID_SIZE) * GRID_SIZE,
  };
}

/**
 * Convert screen coordinates to canvas coordinates
 */
export function screenToCanvas(screenX, screenY, containerRef, pan, zoom) {
  if (!containerRef?.current) return { x: 0, y: 0 };
  const rect = containerRef.current.getBoundingClientRect();
  return {
    x: (screenX - rect.left - pan.x) / zoom,
    y: (screenY - rect.top - pan.y) / zoom,
  };
}

/**
 * Pan delta (screen px, same space as CSS translate) when the pointer is inside a
 * feathered band along the viewport edge — used for auto-scroll while dragging connections.
 *
 * @param {number} clientX
 * @param {number} clientY
 * @param {DOMRect} containerRect - getBoundingClientRect() of the flowchart container
 * @param {{ margin?: number, maxSpeed?: number }} [options]
 * @returns {{ x: number, y: number }}
 */
export function computeEdgeAutoPanDelta(clientX, clientY, containerRect, options = {}) {
  const margin = options.margin ?? 72;
  const maxSpeed = options.maxSpeed ?? 22;

  const mx = clientX - containerRect.left;
  const my = clientY - containerRect.top;
  const w = containerRect.width;
  const h = containerRect.height;

  if (w <= 0 || h <= 0) return { x: 0, y: 0 };

  let dx = 0;
  let dy = 0;

  if (mx < margin) {
    const t = (margin - mx) / margin;
    dx = t * t * maxSpeed;
  } else if (mx > w - margin) {
    const t = (mx - (w - margin)) / margin;
    dx = -t * t * maxSpeed;
  }

  if (my < margin) {
    const t = (margin - my) / margin;
    dy = t * t * maxSpeed;
  } else if (my > h - margin) {
    const t = (my - (h - margin)) / margin;
    dy = -t * t * maxSpeed;
  }

  return { x: dx, y: dy };
}

/**
 * Get node dimensions with fallback
 */
export function getNodeDimensions(taskId, nodeDimensions) {
  return nodeDimensions[taskId] || { width: NODE_WIDTH, height: NODE_HEIGHT };
}

